import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Wrench, X, ChevronRight, ChevronLeft, Check, Gauge, Clock, CalendarDays } from "lucide-react";

const PRESETS = ["Vidange", "Révision", "Pneus", "Contrôle technique", "Freinage", "Autre"];
const PRESET_TYPE = { "Vidange": "vidange", "Révision": "revision", "Pneus": "pneus", "Contrôle technique": "controle_technique", "Freinage": "freinage", "Autre": "autre" };
const STEPS = ["Type", "Véhicule", "Entretien", "Confirmation"];
const INP = "w-full bg-secondary/60 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-[#17A2B8]";
const fmt = (n) => Number(n || 0).toLocaleString("fr-FR");

export default function MaintenanceWizard({ open, onClose, onCreated, vehicles: vehiclesProp = [], lockedVehicleId = null }) {
  const [step, setStep] = useState(0);
  const [vehicles, setVehicles] = useState(vehiclesProp);
  const [vid, setVid] = useState(lockedVehicleId || "");
  const [q, setQ] = useState("");
  const [label, setLabel] = useState("");
  const [mode, setMode] = useState("counter");      // counter | date
  const [kmSource, setKmSource] = useState("canbus"); // Item 1 — platform | canbus
  const [relative, setRelative] = useState(true);    // compteur : dans X (relatif) vs à X (absolu)
  const [val, setVal] = useState("");
  const [dateVal, setDateVal] = useState("");
  const [notice, setNotice] = useState("");
  const [repeat, setRepeat] = useState(false);
  const [recur, setRecur] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = useCallback(() => {
    setStep(0); setVid(lockedVehicleId || ""); setQ(""); setLabel(""); setMode("counter"); setKmSource("canbus");
    setRelative(true); setVal(""); setDateVal(""); setNotice(""); setRepeat(false); setRecur(""); setSaving(false);
  }, [lockedVehicleId]);

  useEffect(() => { if (open) reset(); }, [open, reset]);
  useEffect(() => {
    if (!open) return;
    api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => setVehicles(vehiclesProp || []));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const veh = vehicles.find((v) => v.id === vid) || null;
  const isH = veh?.usage_metric === "engine_hours";
  const unit = isH ? "h" : "km";
  // Item 1 — km CANbus réel disponible uniquement si l'odomètre du véhicule est nativement CAN.
  const decodedCanKm = veh?.last_position?.can?.total_mileage_km;
  const nativeCanKm = veh?.odometer_source === "can" ? veh?.odometer_km : null;
  const candidateCanKm = decodedCanKm ?? nativeCanKm;
  const canAvailable = !isH && typeof candidateCanKm === "number" && Number.isFinite(candidateCanKm) && candidateCanKm > 0;
  const canKm = canAvailable ? candidateCanKm : null;
  const canObservedAt = veh?.can_odometer_at || (veh?.odometer_source === "can" ? veh?.last_gps_at : null);
  const canAge = canObservedAt ? (Date.now() - Date.parse(canObservedAt)) : null;
  const canStale = canAge == null || !Number.isFinite(canAge) || canAge < 0 || canAge > 3 * 86400 * 1000;
  const effKmSource = (kmSource === "canbus" && canAvailable) ? "canbus" : "platform";
  const current = isH ? (veh?.engine_hours || 0) : (effKmSource === "canbus" ? (canKm || 0) : (veh?.odometer_km || 0));
  const dueCounter = relative ? current + Number(val || 0) : Number(val || 0);

  const filtered = vehicles.filter((v) => {
    const n = q.trim().toLowerCase();
    return !n || `${v.name} ${v.plate || ""}`.toLowerCase().includes(n);
  });

  const canNext = () => {
    if (step === 0) return true;
    if (step === 1) return !!vid;
    if (step === 2) {
      if (!label.trim()) return false;
      if (mode === "counter") return val !== "" && Number(val) > 0;
      return !!dateVal;
    }
    return true;
  };

  const summary = () => {
    const name = veh ? `${veh.name}${veh.plate ? " " + veh.plate : ""}` : "";
    const lines = [`${label || "Entretien"} — ${name}`];
    if (mode === "counter") {
      lines.push(`Alerte à ${fmt(dueCounter)} ${unit}`);
      if (!isH) lines.push(`Source : ${effKmSource === "canbus" ? "kilométrage CANbus" : "compteur plateforme"}`);
      if (notice) lines.push(`Préavis à ${fmt(dueCounter - Number(notice))} ${unit}`);
      if (repeat && recur) lines.push(`Puis tous les ${fmt(recur)} ${unit}`);
    } else {
      lines.push(`Le ${dateVal.split("-").reverse().join("/")}`);
      if (notice) lines.push(`Préavis ${notice} jour${Number(notice) > 1 ? "s" : ""} avant`);
      if (repeat && recur) lines.push(`Récurrence tous les ${recur} jour${Number(recur) > 1 ? "s" : ""}`);
    }
    return lines;
  };

  const doCreate = async () => {
    if (!vid) return;
    setSaving(true);
    const body = { vehicle_id: vid, m_type: PRESET_TYPE[label] || "autre", label: label.trim() || "Entretien" };
    if (mode === "counter") {
      if (isH) { body.due_hours = dueCounter; if (notice) body.notice_hours = Number(notice); if (repeat && recur) body.recurrence_hours = Number(recur); }
      else { body.due_km = dueCounter; body.km_source = effKmSource; if (notice) body.notice_km = Number(notice); if (repeat && recur) body.recurrence_km = Number(recur); }
    } else {
      body.due_date = dateVal; if (notice) body.notice_days = Number(notice); if (repeat && recur) body.recurrence_days = Number(recur);
    }
    try {
      await api.post("/maintenance", body);
      toast.success("Entretien planifié ✓");
      onCreated && onCreated();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la création"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-3 sm:p-6" data-testid="maintenance-wizard" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl w-full max-w-lg my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-extrabold text-lg flex items-center gap-2"><Wrench className="w-5 h-5 brand-orange" /> Planifier un entretien</h2>
          <button onClick={onClose} data-testid="maint-wizard-close" className="text-muted-foreground hover:text-foreground p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-1 px-5 py-3 border-b border-border overflow-x-auto" data-testid="maint-wizard-progress">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1 shrink-0">
              <span className={`text-xs font-bold px-2 py-0.5 rounded ${i === step ? "bg-[#17A2B8] text-white" : i < step ? "text-[#17A2B8]" : "text-muted-foreground"}`}>{i + 1}. {s}</span>
              {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
            </div>
          ))}
        </div>

        <div className="p-5 min-h-[260px]">
          {/* Étape 1 — Type */}
          {step === 0 && (
            <div data-testid="maint-step-type">
              <div className="border-2 border-[#17A2B8] bg-[#17A2B8]/5 rounded-xl p-4 flex gap-3 items-start">
                <span className="shrink-0 w-10 h-10 rounded-lg bg-[#F5821F]/15 flex items-center justify-center"><Wrench className="w-5 h-5 brand-orange" /></span>
                <span><span className="font-bold text-sm block">Maintenance</span><span className="text-xs text-muted-foreground">Être alerté avant une vidange, révision ou autre entretien</span></span>
                <Check className="w-5 h-5 text-[#17A2B8] ml-auto" />
              </div>
              <p className="text-xs text-muted-foreground mt-3">Le suivi d'entretien gère le préavis, l'échéance et la récurrence, puis vous prévient. Aucune alerte en doublon n'est créée.</p>
            </div>
          )}

          {/* Étape 2 — Véhicule */}
          {step === 1 && (
            <div className="space-y-3" data-testid="maint-step-vehicle">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un véhicule…" data-testid="maint-vehicle-search" className={INP} />
              <div className="max-h-[260px] overflow-y-auto space-y-2 pr-1">
                {filtered.map((v) => {
                  const h = v.usage_metric === "engine_hours";
                  const cur = h ? (v.engine_hours || 0) : (v.odometer_km || 0);
                  return (
                    <button key={v.id} onClick={() => setVid(v.id)} data-testid={`maint-vehicle-${v.id}`}
                      className={`w-full text-left border rounded-xl p-3 flex items-center gap-3 transition-colors ${vid === v.id ? "border-[#17A2B8] bg-[#17A2B8]/5" : "border-border hover:border-[#17A2B8]/60"}`}>
                      <span className="min-w-0 flex-1">
                        <span className="font-bold text-sm block truncate">{v.name}</span>
                        <span className="text-xs text-muted-foreground data">{v.plate || "—"} · {h ? <Clock className="inline w-3 h-3" /> : <Gauge className="inline w-3 h-3" />} {fmt(cur)} {h ? "h" : "km"}</span>
                      </span>
                      {vid === v.id && <Check className="w-4 h-4 text-[#17A2B8] shrink-0" />}
                    </button>
                  );
                })}
                {filtered.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Aucun véhicule.</p>}
              </div>
            </div>
          )}

          {/* Étape 3 — Entretien */}
          {step === 2 && veh && (
            <div className="space-y-4" data-testid="maint-step-detail">
              <div>
                <p className="label-mono mb-1.5">Entretien</p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {PRESETS.map((p) => (
                    <button key={p} onClick={() => setLabel(p)} data-testid={`maint-preset-${PRESET_TYPE[p]}`}
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${label === p ? "border-[#17A2B8] bg-[#17A2B8]/10 text-[#17A2B8]" : "border-border text-muted-foreground hover:border-[#17A2B8]/60"}`}>{p}</button>
                  ))}
                </div>
                <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Libellé de l'entretien" data-testid="maint-label-input" className={INP} />
              </div>

              <div>
                <p className="label-mono mb-1.5">Échéance</p>
                <div className="flex gap-2 mb-2">
                  <button onClick={() => setMode("counter")} data-testid="maint-mode-counter" className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold py-2 rounded-md border ${mode === "counter" ? "border-[#17A2B8] bg-[#17A2B8]/10 text-[#17A2B8]" : "border-border text-muted-foreground"}`}>{isH ? <Clock className="w-4 h-4" /> : <Gauge className="w-4 h-4" />} Compteur ({unit})</button>
                  <button onClick={() => setMode("date")} data-testid="maint-mode-date" className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold py-2 rounded-md border ${mode === "date" ? "border-[#17A2B8] bg-[#17A2B8]/10 text-[#17A2B8]" : "border-border text-muted-foreground"}`}><CalendarDays className="w-4 h-4" /> Date</button>
                </div>

                {mode === "counter" ? (
                  <div className="space-y-2">
                    {!isH && (
                      <div data-testid="maint-km-source">
                        <p className="label-mono mb-1.5">Source du kilométrage</p>
                        <div className="flex gap-2">
                          <button onClick={() => setKmSource("platform")} data-testid="maint-kmsrc-platform"
                            className={`flex-1 text-xs font-semibold py-2 rounded-md border transition-colors ${effKmSource === "platform" ? "border-[#17A2B8] bg-[#17A2B8]/10 text-[#17A2B8]" : "border-border text-muted-foreground"}`}>
                            Compteur plateforme
                          </button>
                          <button onClick={() => canAvailable && setKmSource("canbus")} disabled={!canAvailable} data-testid="maint-kmsrc-canbus"
                            title={canAvailable ? "" : "Kilométrage CANbus indisponible pour ce véhicule"}
                            className={`flex-1 text-xs font-semibold py-2 rounded-md border transition-colors ${!canAvailable ? "border-border text-muted-foreground/50 cursor-not-allowed" : effKmSource === "canbus" ? "border-[#17A2B8] bg-[#17A2B8]/10 text-[#17A2B8]" : "border-border text-muted-foreground"}`}>
                            Kilométrage CANbus
                          </button>
                        </div>
                        {!canAvailable && <p className="text-[11px] text-amber-600 mt-1" data-testid="maint-can-unavailable">Kilométrage CANbus indisponible pour ce véhicule (odomètre non CAN). Le compteur plateforme est utilisé.</p>}
                        {canAvailable && canStale && <p className="text-[11px] text-amber-600 mt-1" data-testid="maint-can-stale">⚠ Fraîcheur CANbus non confirmée — vérifier la date de la dernière lecture.</p>}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground" data-testid="maint-current-counter">Compteur actuel ({effKmSource === "canbus" ? "CANbus" : "plateforme"}) : <b className="data text-foreground">{fmt(current)} {unit}</b></p>
                    <div className="flex gap-2">
                      <button onClick={() => setRelative(true)} data-testid="maint-rel-relative" className={`flex-1 text-xs font-semibold py-1.5 rounded border ${relative ? "border-[#17A2B8] text-[#17A2B8]" : "border-border text-muted-foreground"}`}>Dans X {unit}</button>
                      <button onClick={() => setRelative(false)} data-testid="maint-rel-absolute" className={`flex-1 text-xs font-semibold py-1.5 rounded border ${!relative ? "border-[#17A2B8] text-[#17A2B8]" : "border-border text-muted-foreground"}`}>À un {isH ? "nombre d'heures" : "kilométrage"}</button>
                    </div>
                    <input type="number" value={val} onChange={(e) => setVal(e.target.value)} data-testid="maint-due-value"
                      placeholder={relative ? `Nombre de ${unit} avant l'entretien` : `${isH ? "Heures" : "Kilométrage"} de l'échéance`} className={INP} />
                    {val !== "" && Number(val) > 0 && <p className="text-xs text-[#17A2B8]" data-testid="maint-due-computed">→ Échéance à {fmt(dueCounter)} {unit}</p>}
                  </div>
                ) : (
                  <input type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} data-testid="maint-due-date" className={INP} />
                )}
              </div>

              <div>
                <p className="label-mono mb-1.5">Préavis (facultatif)</p>
                <input type="number" value={notice} onChange={(e) => setNotice(e.target.value)} data-testid="maint-notice"
                  placeholder={mode === "counter" ? `Me prévenir X ${unit} avant` : "Me prévenir X jours avant"} className={INP} />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} data-testid="maint-repeat-toggle" className="w-4 h-4 accent-[#17A2B8]" />
                  Répéter automatiquement après réalisation
                </label>
                {repeat && (
                  <input type="number" value={recur} onChange={(e) => setRecur(e.target.value)} data-testid="maint-recur"
                    placeholder={mode === "counter" ? `Tous les X ${unit}` : "Tous les X jours"} className={`${INP} mt-2`} />
                )}
              </div>
            </div>
          )}

          {/* Étape 4 — Confirmation */}
          {step === 3 && veh && (
            <div className="space-y-3" data-testid="maint-step-confirm">
              <div className="rounded-xl border border-[#17A2B8]/40 bg-[#17A2B8]/5 p-4">
                {summary().map((l, i) => (
                  <p key={i} className={i === 0 ? "font-bold text-base" : "text-sm text-muted-foreground"} data-testid={`maint-summary-line-${i}`}>{l}</p>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Cet entretien sera suivi automatiquement : un préavis d'abord, puis une alerte à l'échéance, visibles dans l'onglet Maintenance du véhicule et sur la page Maintenance.</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-border">
          <button onClick={step === 0 ? onClose : () => setStep(step - 1)} data-testid="maint-wizard-back" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground px-3 py-2">
            <ChevronLeft className="w-4 h-4" /> {step === 0 ? "Annuler" : "Retour"}
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={() => setStep(step + 1)} disabled={!canNext()} data-testid="maint-wizard-next"
              className="flex items-center gap-1 bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-40 text-white text-sm font-bold px-5 py-2 rounded-md">Suivant <ChevronRight className="w-4 h-4" /></button>
          ) : (
            <button onClick={doCreate} disabled={saving} data-testid="maint-wizard-create"
              className="flex items-center gap-1 bg-[#10B981] hover:bg-[#0ea371] disabled:opacity-40 text-white text-sm font-bold px-5 py-2 rounded-md"><Check className="w-4 h-4" /> {saving ? "Création…" : "Planifier"}</button>
          )}
        </div>
      </div>
    </div>
  );
}
